import { useState, useMemo, useCallback } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  alpha,
  useTheme,
  InputAdornment,
  Chip,
} from "@mui/material";
import {
  Search,
  Router,
  LabelOutlined,
  AddAlarm,
  Person,
  Fence,
  Dashboard,
  Description,
  CorporateFare,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDevices } from "../../hooks/queries/useDevices";
import useLabels from "../../hooks/useLabels";
import { useActions } from "../../hooks/queries/useActions";
import { useContacts } from "../../hooks/queries/useContacts";
import { useGeofences } from "../../hooks/queries/useGeofences";
import { useDashboards } from "../../hooks/queries/useDashboards";
import { useReports } from "../../hooks/queries/useReports";
import { useOrganisations } from "../../hooks/queries/useOrganisations";

const CATEGORY_CONFIG = {
  device: { icon: Router, labelKey: "common.devices", color: "#3B82F6" },
  label: { icon: LabelOutlined, labelKey: "labels.index", color: "#8B5CF6" },
  action: { icon: AddAlarm, labelKey: "common.actions", color: "#F97316" },
  contact: { icon: Person, labelKey: "common.contacts", color: "#22C55E" },
  geofence: { icon: Fence, labelKey: "common.geofences", color: "#14B8A6" },
  dashboard: {
    icon: Dashboard,
    labelKey: "common.dashboards",
    color: "#06B6D4",
  },
  report: { icon: Description, labelKey: "common.reports", color: "#EC4899" },
  organisation: {
    icon: CorporateFare,
    labelKey: "common.organisations",
    color: "#64748B",
  },
};

export default function GlobalSearch() {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");

  const selectedOrganisation = useSelector(
    (state) => state.selectedOrganisation
  );
  const orgId = selectedOrganisation?.id;

  const { data: devices = [] } = useDevices(orgId);
  const { data: labels = [] } = useLabels(orgId);
  const { data: actions = [] } = useActions(orgId);
  const { data: contacts = [] } = useContacts(orgId);
  const { data: geofences = [] } = useGeofences(orgId);
  const { data: dashboards = [] } = useDashboards(orgId);
  const { data: reports = [] } = useReports(orgId);
  const { data: organisations = [] } = useOrganisations();

  const allOptions = useMemo(() => {
    const opts = [];

    devices.forEach((d) => {
      const tagNames = d.tags?.map((tag) => tag.name).join(" ") || "";
      opts.push({
        id: d.id,
        category: "device",
        label: d.name || d.dev_eui,
        secondary: d.dev_eui,
        path: `/device/${d.id}`,
        searchText:
          `${d.name} ${d.dev_eui} ${d.description || ""} ${tagNames}`.toLowerCase(),
        tags: d.tags,
      });
    });

    labels.forEach((l) =>
      opts.push({
        id: l.id,
        category: "label",
        label: l.name,
        path: `/label/${l.id}`,
        searchText: l.name.toLowerCase(),
        color: l.color,
      })
    );

    actions.forEach((a) =>
      opts.push({
        id: a.id,
        category: "action",
        label: a.name,
        path: `/action/${a.id}/logs`,
        searchText: a.name.toLowerCase(),
      })
    );

    contacts.forEach((c) =>
      opts.push({
        id: c.id,
        category: "contact",
        label: c.name || c.email,
        secondary: c.email,
        path: `/contact?editContactId=${c.id}`,
        searchText: `${c.name || ""} ${c.email || ""}`.toLowerCase(),
      })
    );

    geofences.forEach((g) =>
      opts.push({
        id: g.id,
        category: "geofence",
        label: g.name,
        path: `/geofence/${g.id}`,
        searchText: g.name.toLowerCase(),
      })
    );

    dashboards.forEach((d) =>
      opts.push({
        id: d.id,
        category: "dashboard",
        label: d.name,
        path: `/dashboard/${d.id}`,
        searchText: d.name.toLowerCase(),
      })
    );

    reports.forEach((r) =>
      opts.push({
        id: r.id,
        category: "report",
        label: r.name,
        path: `/report/${r.id}`,
        searchText: r.name.toLowerCase(),
      })
    );

    organisations.forEach((o) =>
      opts.push({
        id: o.id,
        category: "organisation",
        label: o.name,
        path: `/organisation/${o.id}`,
        searchText: o.name.toLowerCase(),
      })
    );

    return opts;
  }, [
    devices,
    labels,
    actions,
    contacts,
    geofences,
    dashboards,
    reports,
    organisations,
  ]);

  const filteredOptions = useMemo(() => {
    if (!inputValue.trim()) return [];
    const terms = inputValue.toLowerCase().split(/\s+/);
    return allOptions
      .filter((opt) => terms.every((term) => opt.searchText.includes(term)))
      .slice(0, 20);
  }, [inputValue, allOptions]);

  const handleSelect = useCallback(
    (_event, option) => {
      if (option?.path) {
        navigate(option.path);
        setInputValue("");
      }
    },
    [navigate]
  );

  return (
    <Autocomplete
      freeSolo
      size="small"
      options={filteredOptions}
      inputValue={inputValue}
      onInputChange={(_e, val) => setInputValue(val)}
      onChange={handleSelect}
      value={null}
      getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.label)}
      isOptionEqualToValue={(opt, val) =>
        opt.id === val.id && opt.category === val.category
      }
      groupBy={(opt) => opt.category}
      renderGroup={(params) => {
        const config = CATEGORY_CONFIG[params.group];
        const Icon = config?.icon;
        return (
          <li key={params.key}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.75,
                backgroundColor: alpha(
                  config?.color || theme.palette.primary.main,
                  0.08
                ),
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              }}
            >
              {Icon && (
                <Icon
                  sx={{
                    fontSize: 16,
                    color: config.color,
                  }}
                />
              )}
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: config?.color || theme.palette.text.secondary,
                  fontSize: "0.68rem",
                }}
              >
                {t(config?.labelKey || params.group)}
              </Typography>
            </Box>
            <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
          </li>
        );
      }}
      renderOption={({ key: _key, ...restProps }, option) => {
        return (
          <Box
            component="li"
            key={`${option.category}-${option.id}`}
            {...restProps}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2,
              py: 1,
              cursor: "pointer",
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {option.label}
              </Typography>
              {option.secondary && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontFamily:
                      option.category === "device" ? "monospace" : undefined,
                    fontSize: "0.7rem",
                  }}
                >
                  {option.secondary}
                </Typography>
              )}
              {option.tags?.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    mt: 0.25,
                    flexWrap: "wrap",
                  }}
                >
                  {option.tags.map((tag) => (
                    <Chip
                      key={tag.id}
                      label={tag.name}
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: "0.6rem",
                        fontWeight: 600,
                        borderRadius: "16px",
                        backgroundColor: tag.color
                          ? alpha(tag.color, 0.15)
                          : alpha(theme.palette.primary.main, 0.15),
                        color: tag.color || theme.palette.primary.main,
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>
            {option.category === "label" && option.color && (
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: option.color,
                  flexShrink: 0,
                }}
              />
            )}
          </Box>
        );
      }}
      slotProps={{
        popper: {
          sx: {
            "& .MuiAutocomplete-paper": {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
              border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
              mt: 0.5,
            },
            "& .MuiAutocomplete-listbox": {
              py: 0,
              maxHeight: 400,
            },
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t("search.placeholder")}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    sx={{
                      fontSize: 18,
                      color: alpha(theme.palette.text.secondary, 0.5),
                    }}
                  />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              fontSize: "0.82rem",
              height: 36,
              transition: "all 0.2s ease",
              "& fieldset": {
                borderColor: alpha(theme.palette.divider, 0.2),
              },
              "&:hover fieldset": {
                borderColor: alpha(theme.palette.primary.main, 0.3),
              },
              "&.Mui-focused": {
                backgroundColor: theme.palette.background.paper,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
            "& .MuiOutlinedInput-input::placeholder": {
              color: alpha(theme.palette.text.secondary, 0.5),
              opacity: 1,
            },
          }}
        />
      )}
    />
  );
}
